import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Typography,
  Avatar,
  CircularProgress,
  Backdrop,
  Alert,
  FormControl,
  Card,
  Stepper,
  Step,
  StepLabel,
  Box,
  Button as MuiButton,
  Grid,
} from "@mui/material";
import PageLayout from "examples/LayoutContainers/PageLayout";
import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftSelect from "components/SoftSelect";
import { useSearchParams } from "react-router-dom";
import { CloudUpload, PhotoCamera } from "@mui/icons-material";

const API_BASE = "https://surveys.exchange-ip.com/api";

function BasicInformation() {
  const [activeStep, setActiveStep] = useState(0);
  const steps = ["Basic Info", "Education", "Documents"];

  // Basic Information Form State
  const [basicInfo, setBasicInfo] = useState({
    name: "",
    last_name: "",
    gender: "",
    dob: "",
    phone_number: "",
    email: "",
    qualification: "",
    location: "",
    employment_status: "",
    salary_expected: "",
    relocate: "",
    referrer_name: "",
    resume: null,
    photo: null,
  });

  // Educational Details State
  const [educationalDetails, setEducationalDetails] = useState({
    tenth_exam_passed: "",
    tenth_board: "",
    tenth_institution_name: "",
    tenth_year_of_passing: "",
    tenth_percentage: "",
    tenth_subject: "",
    tenth_mode: "",
    twelth_exam_passed: "",
    twelth_board: "",
    twelth_institution_name: "",
    twelth_year_of_passing: "",
    twelth_percentage: "",
    twelth_subject: "",
    twelth_mode: "",
    graduation_exam_passed: "",
    graduation_board: "",
    graduation_institution_name: "",
    graduation_year_of_passing: "",
    graduation_percentage: "",
    graduation_subject: "",
    graduation_mode: "",
    post_graduation_exam_passed: "",
    post_graduation_board: "",
    post_graduation_institution_name: "",
    post_graduation_year_of_passing: "",
    post_graduation_percentage: "",
    post_graduation_subject: "",
    post_graduation_mode: "",
  });

  // Upload Documents State
  const [uploadDocuments, setUploadDocuments] = useState({
    resume: null,
    photo: null,
    aadhar_card: null,
    pan_card: null,
    tenth_marksheet: null,
    twelth_marksheet: null,
    graduation_certificate: null,
    post_graduation_certificate: null,
    experience_letter: null,
    salary_slip: null,
  });

  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [formErrors, setFormErrors] = useState({});
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState({ type: "", message: "" });
  const [tokenValid, setTokenValid] = useState(true);
  const [previewUrls, setPreviewUrls] = useState({});

  // File upload fields configuration
  const documentFields = [
    // { label: "Resume", key: "resume", required: true },
    // { label: "Photo", key: "photo", required: true },
    { label: "Aadhar Card", key: "aadhar_card", required: false },
    { label: "PAN Card", key: "pan_card", required: false },
    { label: "10th Marksheet", key: "upload_tenth", required: true },
    { label: "12th Marksheet", key: "upload_twelth", required: true },
    { label: "Graduation Certificate", key: "graduation_certificate", required: false },
    { label: "Post Graduation Certificate", key: "post_graduation_certificate", required: false },
    { label: "Experience Letter", key: "experience_letter", required: false },
    { label: "Salary Slip", key: "salary_slip", required: false },
  ];

  // Educational Levels - ALL FIELDS REQUIRED
  const educationLevels = [
    { key: "tenth", label: "10th Details *", required: true },
    { key: "twelth", label: "12th Details *", required: true },
    { key: "graduation", label: "Graduation Details (Optional)", required: false },
    { key: "post_graduation", label: "Post Graduation Details (Optional)", required: false },
  ];

  const formatPhone = (value) => {
    const onlyNums = value.replace(/[^\d]/g, "");
    if (onlyNums.length <= 3) return onlyNums;
    if (onlyNums.length <= 6) return `${onlyNums.slice(0, 3)}-${onlyNums.slice(3)}`;
    return `${onlyNums.slice(0, 3)}-${onlyNums.slice(3, 6)}-${onlyNums.slice(6, 10)}`;
  };

  // Handle Basic Information Changes
  const handleBasicInfoChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone_number") {
      setBasicInfo((prev) => ({ ...prev, [name]: formatPhone(value) }));
    } else {
      setBasicInfo((prev) => ({ ...prev, [name]: value }));
    }
  };

  const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

  const handleBasicInfoFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "File size must be less than 2MB",
      }));
      e.target.value = ""; // reset input
      return;
    }

    setFormErrors((prev) => ({ ...prev, [name]: "" }));

    setBasicInfo((prev) => ({ ...prev, [name]: file }));

    if (name === "photo") {
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  // Handle Educational Details Changes
  const handleEducationalChange = (key, value) => {
    setEducationalDetails((prev) => ({ ...prev, [key]: value }));
  };

  // Handle Upload Documents Changes
  const handleDocumentUpload = (key, file) => {
    if (!(file instanceof File)) return;

    if (file.size > MAX_FILE_SIZE) {
      setFormErrors((prev) => ({
        ...prev,
        [key]: "File size must be less than 2MB",
      }));
      return;
    }

    setFormErrors((prev) => ({ ...prev, [key]: "" }));

    setUploadDocuments((prev) => ({
      ...prev,
      [key]: file,
    }));

    setPreviewUrls((prev) => ({
      ...prev,
      [key]: URL.createObjectURL(file),
    }));
  };

  const validateBasicInfo = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;

    // ALL FIELDS ARE REQUIRED
    if (!basicInfo.name.trim()) errors.name = "First Name is required";
    if (!basicInfo.last_name.trim()) errors.last_name = "Last Name is required";
    if (!basicInfo.gender) errors.gender = "Gender is required";
    if (!basicInfo.dob) errors.dob = "Date of Birth is required";
    if (!basicInfo.phone_number) errors.phone_number = "Phone Number is required";
    else if (!phoneRegex.test(basicInfo.phone_number)) errors.phone_number = "Format: 987-654-3210";
    if (!basicInfo.email) errors.email = "Email is required";
    else if (!emailRegex.test(basicInfo.email)) errors.email = "Invalid email format";
    if (!basicInfo.qualification.trim()) errors.qualification = "Qualification is required";
    if (!basicInfo.location.trim()) errors.location = "Location is required";
    if (!basicInfo.employment_status) errors.employment_status = "Employment Status is required";
    if (!basicInfo.salary_expected) errors.salary_expected = "Expected Salary is required";
    if (!basicInfo.relocate) errors.relocate = "Relocation status is required";
    if (!basicInfo.referrer_name.trim()) errors.referrer_name = "Referrer Name is required";
    if (!basicInfo.resume) errors.resume = "Resume is required";
    if (!basicInfo.photo) errors.photo = "Photo is required";

    return errors;
  };

  const validateEducationDetails = () => {
    const errors = {};

    educationLevels.forEach(({ key, required }) => {
      if (!required) return; // 🔥 graduation & post graduation skip

      if (!educationalDetails[`${key}_exam_passed`]?.trim())
        errors[`${key}_exam_passed`] = `${key.toUpperCase()} Exam Passed is required`;

      if (!educationalDetails[`${key}_board`]?.trim())
        errors[`${key}_board`] = `${key.toUpperCase()} Board/University is required`;

      if (!educationalDetails[`${key}_institution_name`]?.trim())
        errors[`${key}_institution_name`] = `${key.toUpperCase()} Institution Name is required`;

      if (!educationalDetails[`${key}_year_of_passing`])
        errors[`${key}_year_of_passing`] = `${key.toUpperCase()} Year of Passing is required`;

      if (!educationalDetails[`${key}_percentage`]?.trim())
        errors[`${key}_percentage`] = `${key.toUpperCase()} Percentage is required`;

      if (!educationalDetails[`${key}_subject`]?.trim())
        errors[`${key}_subject`] = `${key.toUpperCase()} Subject is required`;

      if (!educationalDetails[`${key}_mode`]?.trim())
        errors[`${key}_mode`] = `${key.toUpperCase()} Mode is required`;
    });

    return errors;
  };

  const validateDocuments = () => {
    const errors = {};

    documentFields.forEach(({ key, label, required }) => {
      if (required && !uploadDocuments[key]) {
        errors[key] = `${label} is required`;
      }
    });

    return errors;
  };

  const handleNext = () => {
    let errors = {};

    if (activeStep === 0) {
      errors = validateBasicInfo();
    } else if (activeStep === 1) {
      errors = validateEducationDetails();
    } else if (activeStep === 2) {
      errors = validateDocuments();
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);

      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus();
      }

      return;
    }

    setFormErrors({});
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all steps before submitting
    const basicErrors = validateBasicInfo();
    const educationErrors = validateEducationDetails();
    const documentErrors = validateDocuments();

    const allErrors = { ...basicErrors, ...educationErrors, ...documentErrors };

    if (Object.keys(allErrors).length > 0) {
      setFormErrors(allErrors);
      setAlert({
        type: "error",
        message: "Please fill all required fields before submitting.",
      });

      // Scroll to first error
      const firstErrorField = Object.keys(allErrors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus();
      }

      return;
    }

    setFormErrors({});
    setAlert({ type: "", message: "" });

    try {
      setSubmitting(true);

      const payload = new FormData();

      // Add basic info
      Object.keys(basicInfo).forEach((key) => {
        if (basicInfo[key] !== null && basicInfo[key] !== "") {
          payload.append(key, basicInfo[key]);
        }
      });

      // Add educational details
      Object.keys(educationalDetails).forEach((key) => {
        if (educationalDetails[key] !== null && educationalDetails[key] !== "") {
          payload.append(key, educationalDetails[key]);
        }
      });

      // Add upload documents
      Object.keys(uploadDocuments).forEach((key) => {
        if (uploadDocuments[key] !== null && uploadDocuments[key] !== "") {
          payload.append(key, uploadDocuments[key]);
        }
      });

      payload.append("token", token);

      const res = await axios.post(
        `https://surveys.exchange-ip.com/api/interview-evaluation`,
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (res.data.success) {
        setAlert({
          type: "success",
          message: res.data.message || "Form submitted successfully!",
        });

        // Reset all forms
        setBasicInfo({
          name: "",
          last_name: "",
          gender: "",
          dob: "",
          phone_number: "",
          email: "",
          qualification: "",
          location: "",
          employment_status: "",
          salary_expected: "",
          relocate: "",
          referrer_name: "",
          resume: null,
          photo: null,
        });

        setEducationalDetails({
          tenth_exam_passed: "",
          tenth_board: "",
          tenth_institution_name: "",
          tenth_year_of_passing: "",
          tenth_percentage: "",
          tenth_subject: "",
          tenth_mode: "",
          twelth_exam_passed: "",
          twelth_board: "",
          twelth_institution_name: "",
          twelth_year_of_passing: "",
          twelth_percentage: "",
          twelth_subject: "",
          twelth_mode: "",
          graduation_exam_passed: "",
          graduation_board: "",
          graduation_institution_name: "",
          graduation_year_of_passing: "",
          graduation_percentage: "",
          graduation_subject: "",
          graduation_mode: "",
          post_graduation_exam_passed: "",
          post_graduation_board: "",
          post_graduation_institution_name: "",
          post_graduation_year_of_passing: "",
          post_graduation_percentage: "",
          post_graduation_subject: "",
          post_graduation_mode: "",
        });

        setUploadDocuments({
          resume: null,
          photo: null,
          aadhar_card: null,
          pan_card: null,
          tenth_marksheet: null,
          twelth_marksheet: null,
          graduation_certificate: null,
          post_graduation_certificate: null,
          experience_letter: null,
          salary_slip: null,
        });
        setTokenValid(false);

        setPhotoPreview(null);
        setTokenValid(false);
        setActiveStep(0);
      } else {
        throw new Error(res.data.message || "Submission failed");
      }
    } catch (error) {
      setAlert({
        type: "error",
        message: error.response?.data?.message || error.message || "Failed to submit form.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageLayout>
      <Backdrop open={loading || submitting} style={{ zIndex: 1301 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {!loading && (
        <SoftBox py={2} px={4}>
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-8 col-lg-8 pl-5 ml-5">
                <Card className="card shadow-lg px-3 py-1">
                  <Box sx={{ width: "100%" }} mb={-2}>
                    <Stepper activeStep={activeStep}>
                      {steps.map((label) => (
                        <Step key={label}>
                          <StepLabel>{label}</StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  </Box>

                  {alert.message && (
                    <Alert severity={alert.type} className="mb-2">
                      {alert.message}
                    </Alert>
                  )}

                  {!tokenValid ? (
                    <Alert severity="warning" className="mb-2">
                      This form is not accessible.
                    </Alert>
                  ) : (
                    <div className="ml-4 pl-4">
                      {/* Step 1: Basic Information */}
                      {activeStep === 0 && (
                        <div style={{ marginLeft: "40px" }}>
                          <Typography
                            variant="h5"
                            fontWeight="bold"
                            className="mb-3 text-center"
                            gutterBottom
                          >
                            Basic Information
                          </Typography>

                          <Grid container spacing={2}>
                            {/* Row 1 */}
                            <Grid item xs={12} sm={6} md={3}>
                              <SoftTypography
                                variant="caption"
                                fontWeight="bold"
                                display="block"
                                mb={0.5}
                              >
                                First Name *
                              </SoftTypography>
                              <SoftInput
                                name="name"
                                value={basicInfo.name}
                                onChange={handleBasicInfoChange}
                                error={!!formErrors.name}
                                helperText={formErrors.name}
                                fullWidth
                                size="small"
                                placeholder="Enter first name"
                                required
                              />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                              <SoftTypography
                                variant="caption"
                                fontWeight="bold"
                                display="block"
                                mb={0.5}
                              >
                                Last Name *
                              </SoftTypography>
                              <SoftInput
                                name="last_name"
                                value={basicInfo.last_name}
                                onChange={handleBasicInfoChange}
                                error={!!formErrors.last_name}
                                helperText={formErrors.last_name}
                                fullWidth
                                size="small"
                                placeholder="Enter last name"
                                required
                              />
                            </Grid>


                            <div style={{ width: "calc(195px)" }}>
                              <Grid item xs={12} sm={6} md={3}>
                                <SoftTypography
                                  variant="caption"
                                  fontWeight="bold"
                                  display="block"
                                  mb={0.5}
                                >
                                  Gender *
                                </SoftTypography>
                                <SoftSelect
                                  size="small"
                                  value={
                                    [
                                      { value: "male", label: "Male" },
                                      { value: "female", label: "Female" },
                                      { value: "other", label: "Other" },
                                    ].find((opt) => opt.value === basicInfo.gender) || null
                                  }
                                  onChange={(e) => setBasicInfo({ ...basicInfo, gender: e.value })}
                                  options={[
                                    { value: "male", label: "Male" },
                                    { value: "female", label: "Female" },
                                    { value: "other", label: "Other" },
                                  ]}
                                  error={!!formErrors.gender}
                                  helperText={formErrors.gender}
                                  placeholder="Select gender"
                                  required
                                />
                              </Grid>
                            </div>



                            {/* Row 2 */}
                            <Grid item xs={12} sm={6} md={3}>
                              <SoftTypography
                                variant="caption"
                                fontWeight="bold"
                                display="block"
                                mb={0.5}
                              >
                                Phone Number *
                              </SoftTypography>
                              <SoftInput
                                name="phone_number"
                                value={basicInfo.phone_number}
                                onChange={handleBasicInfoChange}
                                error={!!formErrors.phone_number}
                                helperText={formErrors.phone_number}
                                fullWidth
                                size="small"
                                placeholder="Enter phone number"
                                required
                              />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                              <SoftTypography
                                variant="caption"
                                fontWeight="bold"
                                display="block"
                                mb={0.5}
                              >
                                Email *
                              </SoftTypography>
                              <SoftInput
                                name="email"
                                type="email"
                                value={basicInfo.email}
                                onChange={handleBasicInfoChange}
                                error={!!formErrors.email}
                                helperText={formErrors.email}
                                fullWidth
                                size="small"
                                placeholder="Enter email address"
                                required
                              />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                              <SoftTypography
                                variant="caption"
                                fontWeight="bold"
                                display="block"
                                mb={0.5}
                              >
                                Qualification *
                              </SoftTypography>
                              <SoftInput
                                name="qualification"
                                value={basicInfo.qualification}
                                onChange={handleBasicInfoChange}
                                error={!!formErrors.qualification}
                                helperText={formErrors.qualification}
                                fullWidth
                                size="small"
                                placeholder="e.g., B.Tech, MBA"
                                required
                              />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                              <SoftTypography
                                variant="caption"
                                fontWeight="bold"
                                display="block"
                                mb={0.5}
                              >
                                Location *
                              </SoftTypography>
                              <SoftInput
                                name="location"
                                value={basicInfo.location}
                                onChange={handleBasicInfoChange}
                                error={!!formErrors.location}
                                helperText={formErrors.location}
                                fullWidth
                                size="small"
                                placeholder="Enter current location"
                                required
                              />
                            </Grid>

                            {/* Row 3 */}
                            <Grid item xs={12} sm={6} md={3}>
                              <SoftTypography
                                variant="caption"
                                fontWeight="bold"
                                display="block"
                                mb={0.5}
                              >
                                Referrer Name *
                              </SoftTypography>
                              <SoftInput
                                name="referrer_name"
                                value={basicInfo.referrer_name}
                                onChange={handleBasicInfoChange}
                                error={!!formErrors.referrer_name}
                                helperText={formErrors.referrer_name}
                                fullWidth
                                size="small"
                                placeholder="Enter referrer name"
                                required
                              />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                              <SoftTypography
                                variant="caption"
                                fontWeight="bold"
                                display="block"
                                mb={0.5}
                              >
                                Expected Salary *
                              </SoftTypography>
                              <SoftInput
                                name="salary_expected"
                                value={basicInfo.salary_expected}
                                onChange={handleBasicInfoChange}
                                error={!!formErrors.salary_expected}
                                helperText={formErrors.salary_expected}
                                fullWidth
                                size="small"
                                placeholder="e.g., ₹50,000"
                                required
                              />
                            </Grid>
                            <div style={{ width: "calc(195px)" }}>
                              <Grid item xs={12} sm={6} md={3}>
                                <SoftTypography
                                  variant="caption"
                                  fontWeight="bold"
                                  display="block"
                                  mb={0.5}
                                >
                                  Employment Status *
                                </SoftTypography>
                                <SoftSelect
                                  size="small"
                                  value={
                                    [
                                      { value: "employed", label: "Employed" },
                                      { value: "unemployed", label: "Unemployed" },
                                      { value: "fresher", label: "Fresher" },
                                    ].find((opt) => opt.value === basicInfo.employment_status) || null
                                  }
                                  onChange={(e) =>
                                    setBasicInfo({ ...basicInfo, employment_status: e.value })
                                  }
                                  options={[
                                    { value: "employed", label: "Employed" },
                                    { value: "unemployed", label: "Unemployed" },
                                    { value: "fresher", label: "Fresher" },
                                  ]}
                                  error={!!formErrors.employment_status}
                                  helperText={formErrors.employment_status}
                                  placeholder="Select status"
                                  required
                                />
                              </Grid>
                            </div>
                            <div style={{ width: "calc(195px)" }}>
                              <Grid item xs={12} sm={6} md={3}>
                                <SoftTypography
                                  variant="caption"
                                  fontWeight="bold"
                                  display="block"
                                  mb={0.5}
                                >
                                  Ready to Relocate *
                                </SoftTypography>
                                <SoftSelect
                                  size="small"
                                  value={
                                    [
                                      { value: "yes", label: "Yes" },
                                      { value: "no", label: "No" },
                                    ].find((opt) => opt.value === basicInfo.relocate) || null
                                  }
                                  onChange={(e) => setBasicInfo({ ...basicInfo, relocate: e.value })}
                                  options={[
                                    { value: "yes", label: "Yes" },
                                    { value: "no", label: "No" },
                                  ]}
                                  error={!!formErrors.relocate}
                                  helperText={formErrors.relocate}
                                  placeholder="Select option"
                                  required
                                />
                              </Grid>
                            </div>

                            <div style={{ width: "calc(195px)" }}>
                              <Grid item xs={12} sm={6} md={3}>
                                <SoftTypography
                                  variant="caption"
                                  fontWeight="bold"
                                  display="block"
                                  mb={0.5}
                                >
                                  Date of Birth *
                                </SoftTypography>
                                <SoftInput
                                  name="dob"
                                  type="date"
                                  value={basicInfo.dob}
                                  onChange={handleBasicInfoChange}
                                  error={!!formErrors.dob}
                                  helperText={formErrors.dob}
                                  fullWidth
                                  size="small"
                                  InputLabelProps={{ shrink: true }}
                                  required
                                />
                              </Grid>
                            </div>

                            {/* Row 4 - File Uploads */}
                            <Grid item xs={12} sm={6} md={6}>
                              <SoftTypography
                                variant="caption"
                                fontWeight="bold"
                                display="block"
                                mb={0.5}
                              >
                                Resume *
                              </SoftTypography>
                              <SoftButton
                                variant="outlined"
                                component="label"
                                color="info"
                                fullWidth
                                size="small"
                                error={!!formErrors.resume}
                                startIcon={<CloudUpload />}
                              >
                                Upload Resume
                                <input
                                  type="file"
                                  name="resume"
                                  hidden
                                  accept=".pdf,.doc,.docx"
                                  onChange={handleBasicInfoFileChange}
                                  required
                                />
                              </SoftButton>
                              {basicInfo.resume && (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  mt={0.5}
                                  color="textSecondary"
                                >
                                  📄 {basicInfo.resume.name}
                                </Typography>
                              )}
                              {formErrors.resume && (
                                <Typography variant="caption" color="error">
                                  {formErrors.resume}
                                </Typography>
                              )}
                            </Grid>

                            <Grid item xs={12} sm={6} md={6}>
                              <SoftTypography
                                variant="caption"
                                fontWeight="bold"
                                display="block"
                                mb={0.5}
                              >
                                Photo *
                              </SoftTypography>
                              <SoftButton
                                variant="outlined"
                                component="label"
                                color="info"
                                fullWidth
                                size="small"
                                error={!!formErrors.photo}
                                startIcon={<PhotoCamera />}
                              >
                                Upload Photo
                                <input
                                  type="file"
                                  name="photo"
                                  hidden
                                  accept="image/jpeg,image/png,image/jpg"
                                  onChange={handleBasicInfoFileChange}
                                  required
                                />
                              </SoftButton>
                              {photoPreview && (
                                <SoftBox mt={1} display="flex" justifyContent="center">
                                  <Avatar
                                    src={photoPreview}
                                    alt="Preview"
                                    sx={{
                                      width: 60,
                                      height: 60,
                                      border: 1,
                                      borderColor: "divider",
                                    }}
                                  />
                                </SoftBox>
                              )}
                              {formErrors.photo && (
                                <Typography variant="caption" color="error">
                                  {formErrors.photo}
                                </Typography>
                              )}
                            </Grid>

                            <Grid item xs={12} md={6}>
                              {/* Empty space to maintain layout consistency */}
                            </Grid>
                          </Grid>
                        </div>
                      )}

                      {/* Step 2: Educational Details */}
                      {activeStep === 1 && (
                        <div style={{ marginLeft: "40px" }}>
                          <Typography
                            variant="h5"
                            fontWeight="bold"
                            className="mb-1 text-center"
                            gutterBottom
                          >
                            Educational Details
                          </Typography>

                          {educationLevels.map(({ key, label }) => (
                            <div key={key} className="mb-2">
                              <SoftTypography
                                variant="subtitle2"
                                fontWeight="bold"
                                color="text"
                                className="mb-1"
                              >
                                {label}
                              </SoftTypography>

                              <Grid container spacing={1}>
                                <Grid item xs={12} sm={6} md={3}>
                                  <SoftTypography variant="caption" fontWeight="bold">
                                    Exam Passed *
                                  </SoftTypography>
                                  <SoftInput
                                    size="small"
                                    name={`${key}_exam_passed`}
                                    placeholder={key}
                                    value={educationalDetails[`${key}_exam_passed`] || ""}
                                    onChange={(e) =>
                                      handleEducationalChange(`${key}_exam_passed`, e.target.value)
                                    }
                                    error={!!formErrors[`${key}_exam_passed`]}
                                    helperText={formErrors[`${key}_exam_passed`]}
                                    fullWidth
                                    required
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <SoftTypography variant="caption" fontWeight="bold">
                                    Board/University *
                                  </SoftTypography>
                                  <SoftInput
                                    size="small"
                                    name={`${key}_board`}
                                    placeholder="Board/University"
                                    value={educationalDetails[`${key}_board`] || ""}
                                    onChange={(e) =>
                                      handleEducationalChange(`${key}_board`, e.target.value)
                                    }
                                    error={!!formErrors[`${key}_board`]}
                                    helperText={formErrors[`${key}_board`]}
                                    fullWidth
                                    required
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <SoftTypography variant="caption" fontWeight="bold">
                                    Institution *
                                  </SoftTypography>
                                  <SoftInput
                                    size="small"
                                    name={`${key}_institution_name`}
                                    placeholder="Institution"
                                    value={educationalDetails[`${key}_institution_name`] || ""}
                                    onChange={(e) =>
                                      handleEducationalChange(
                                        `${key}_institution_name`,
                                        e.target.value
                                      )
                                    }
                                    error={!!formErrors[`${key}_institution_name`]}
                                    helperText={formErrors[`${key}_institution_name`]}
                                    fullWidth
                                    required
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <SoftTypography variant="caption" fontWeight="bold">
                                    Year of Passing *
                                  </SoftTypography>
                                  <SoftInput
                                    size="small"
                                    name={`${key}_year_of_passing`}
                                    placeholder="Year"
                                    value={educationalDetails[`${key}_year_of_passing`] || ""}
                                    onChange={(e) =>
                                      handleEducationalChange(
                                        `${key}_year_of_passing`,
                                        e.target.value
                                      )
                                    }
                                    error={!!formErrors[`${key}_year_of_passing`]}
                                    helperText={formErrors[`${key}_year_of_passing`]}
                                    fullWidth
                                    required
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <SoftTypography variant="caption" fontWeight="bold">
                                    Percentage/CGPA *
                                  </SoftTypography>
                                  <SoftInput
                                    size="small"
                                    name={`${key}_percentage`}
                                    placeholder="% or CGPA"
                                    value={educationalDetails[`${key}_percentage`] || ""}
                                    onChange={(e) =>
                                      handleEducationalChange(`${key}_percentage`, e.target.value)
                                    }
                                    error={!!formErrors[`${key}_percentage`]}
                                    helperText={formErrors[`${key}_percentage`]}
                                    fullWidth
                                    required
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <SoftTypography variant="caption" fontWeight="bold">
                                    Subject/Stream *
                                  </SoftTypography>
                                  <SoftInput
                                    size="small"
                                    name={`${key}_subject`}
                                    placeholder="Subject"
                                    value={educationalDetails[`${key}_subject`] || ""}
                                    onChange={(e) =>
                                      handleEducationalChange(`${key}_subject`, e.target.value)
                                    }
                                    error={!!formErrors[`${key}_subject`]}
                                    helperText={formErrors[`${key}_subject`]}
                                    fullWidth
                                    required
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <SoftTypography variant="caption" fontWeight="bold">
                                    Mode *
                                  </SoftTypography>
                                  <SoftInput
                                    size="small"
                                    name={`${key}_mode`}
                                    placeholder="Full/Part-time"
                                    value={educationalDetails[`${key}_mode`] || ""}
                                    onChange={(e) =>
                                      handleEducationalChange(`${key}_mode`, e.target.value)
                                    }
                                    error={!!formErrors[`${key}_mode`]}
                                    helperText={formErrors[`${key}_mode`]}
                                    fullWidth
                                    required
                                  />
                                </Grid>
                              </Grid>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Step 3: Upload Documents */}
                      {activeStep === 2 && (
                        <div style={{ marginLeft: "40px" }}>
                          <Typography
                            variant="h5"
                            fontWeight="bold"
                            className="mb-1 text-center"
                            gutterBottom
                          >
                            Upload Documents
                          </Typography>

                          <Grid container spacing={2}>
                            {documentFields.map(({ label, key, required }) => {
                              const file = uploadDocuments[key];
                              const fileName = file instanceof File ? file.name : null;
                              const error = formErrors[key];

                              return (
                                <Grid item xs={12} sm={6} md={3} key={key}>
                                  <Box
                                    sx={{
                                      width: "225px",
                                      height: "160px", // Fixed height for all boxes
                                      p: 2,
                                      border: "1px solid",
                                      borderColor: error ? "error.main" : "#dee2e6",
                                      borderRadius: "8px",
                                      backgroundColor: error ? "#fff5f5" : "#f8f9fa",
                                      display: "flex",
                                      flexDirection: "column",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      boxSizing: "border-box",
                                    }}
                                  >
                                    {/* Label - Fixed Height Section */}
                                    <Box
                                      sx={{
                                        width: "100%",
                                        height: "40px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        textAlign: "center",
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                          fontSize: "0.75rem",
                                          display: "-webkit-box",
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: "vertical",
                                          overflow: "hidden",
                                          lineHeight: "1.2",
                                          color: error ? "error.main" : "text.primary",
                                          width: "100%",
                                        }}
                                      >
                                        {label} {required && "*"}
                                      </Typography>
                                    </Box>

                                    {/* Upload Button - Fixed Height Section */}
                                    <Box sx={{ width: "100%", height: "36px" }}>
                                      <input
                                        type="file"
                                        hidden
                                        id={`upload-${key}`}
                                        onChange={(e) =>
                                          handleDocumentUpload(key, e.target.files[0])
                                        }
                                        required={required}
                                      />
                                      <label htmlFor={`upload-${key}`} style={{ width: "100%", display: "block" }}>
                                        <SoftButton
                                          variant="contained"
                                          component="span"
                                          size="small"
                                          fullWidth
                                          sx={{
                                            height: "32px",
                                            fontSize: "0.75rem",
                                            textTransform: "none",
                                            fontWeight: 500,
                                            backgroundColor: error ? "error.main" : "info.main",
                                            "&:hover": {
                                              backgroundColor: error ? "error.dark" : "info.dark",
                                            }
                                          }}
                                        >
                                          {file ? "Change File" : "Browse"}
                                        </SoftButton>
                                      </label>
                                    </Box>

                                    {/* File Preview - Fixed Height Section */}
                                    <Box
                                      sx={{
                                        width: "100%",
                                        height: "40px",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        overflow: "hidden",
                                      }}
                                    >
                                      {file ? (
                                        <>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontSize: "0.7rem",
                                              fontWeight: 500,
                                              color: "#28a745",
                                              mb: 0.5,
                                            }}
                                          >
                                            ✓ Uploaded
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontSize: "0.65rem",
                                              whiteSpace: "nowrap",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              maxWidth: "100%",
                                              color: "#6c757d",
                                            }}
                                          >
                                            {fileName}
                                          </Typography>
                                        </>
                                      ) : (
                                        <Typography
                                          variant="caption"
                                          color={error ? "error" : "text.secondary"}
                                          sx={{
                                            fontSize: "0.7rem",
                                            fontStyle: "italic",
                                            textAlign: "center"
                                          }}
                                        >
                                          {error || "No file selected"}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </div>
                      )}

                      {/* Navigation Buttons */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mt: 2,
                          pt: 1,
                          borderTop: "1px solid #eee",
                        }}
                      >
                        <SoftButton
                          type="button"
                          variant="outlined"
                          color="info"
                          disabled={activeStep === 0}
                          onClick={handleBack}
                          size="medium"
                        >
                          Back
                        </SoftButton>

                        {activeStep === steps.length - 1 ? (
                          <SoftButton
                            type="submit"
                            variant="contained"
                            color="info"
                            onClick={handleSubmit}
                            disabled={submitting}
                            size="medium"
                          >
                            {submitting ? "Submitting..." : "Submit"}
                          </SoftButton>
                        ) : (
                          <SoftButton
                            type="button"
                            variant="contained"
                            color="info"
                            onClick={handleNext}
                            size="medium"
                          >
                            Next
                          </SoftButton>
                        )}
                      </Box>
                    </div>
                  )}
                </Card>
              </div>
            </div>
          </div>
        </SoftBox>
      )}
    </PageLayout>
  );
}

export default BasicInformation;
